import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Image,
  SimpleGrid,
  Container,
  Heading,
  useColorModeValue,
  Spinner,
  Center,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
  AlertDescription
} from '@chakra-ui/react';
import { 
  FiSearch, 
  FiFilter, 
  FiTrendingUp, 
  FiStar, 
  FiPlay, 
  FiRefreshCw,
  FiX,
  FiClock,
  FiEye,
  FiHeart
} from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SearchDiscoveryService, type SearchFilters } from '../services/searchDiscoveryService';
import SearchSuggestions from '../components/SearchSuggestions';
import AdvancedSearchModal from '../components/AdvancedSearchModal';
import TrendingContent from '../components/TrendingContent';
import ContentRecommendations from '../components/ContentRecommendations';
import IconWrapper from '../components/IconWrapper';

interface SearchResult {
  id: string;
  title: string;
  description: string;
  category: string;
  poster: string;
  views: number;
  likes: number;
  rating: number;
  year: number;
  genres: string[];
  createdAt: any;
}

const EnhancedSearch: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const initialQuery = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // Tab indices: When hasSearched=true: 0=Search Results, 1=Trending, 2=Recommendations
  // When hasSearched=false: 0=Trending, 1=Recommendations
  const [activeTab, setActiveTab] = useState(initialQuery ? 0 : 0);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<SearchFilters>({});
  const [searchStats, setSearchStats] = useState<{
    totalResults: number;
    searchTime: number;
    filtersApplied: number;
  }>({
    totalResults: 0,
    searchTime: 0,
    filtersApplied: 0
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const headingColor = useColorModeValue('gray.900', 'white');
  const descriptionColor = useColorModeValue('gray.600', 'gray.400');
  const statsColor = useColorModeValue('gray.600', 'gray.400');
  const filterLabelColor = useColorModeValue('gray.600', 'gray.400');

  // Trigger search on initial load if query exists in URL
  useEffect(() => {
    if (initialQuery && initialQuery.trim() && !hasSearched) {
      handleSearch(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger search when URL params change
  useEffect(() => {
    const urlQuery = searchParams.get('q') || '';
    if (urlQuery && urlQuery.trim() && urlQuery !== searchQuery) {
      setSearchQuery(urlQuery);
      handleSearch(urlQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = async (query: string, filters: SearchFilters = {}) => {
    if (!query.trim()) return;

    try {
      setIsSearching(true);
      const startTime = Date.now();
      
      const results = await SearchDiscoveryService.searchContent(
        query,
        filters,
        user?.uid,
        50
      );

      const searchTime = Date.now() - startTime;
      const filtersApplied = Object.values(filters).filter(value => 
        Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null
      ).length;

      setSearchResults(results);
      setSearchQuery(query);
      setCurrentFilters(filters);
      setHasSearched(true);
      setActiveTab(0); // Switch to Search Results tab (index 0 when hasSearched is true)
      setSearchStats({
        totalResults: results.length,
        searchTime,
        filtersApplied
      });

      // Update URL
      const params = new URLSearchParams();
      params.set('q', query);
      setSearchParams(params);

      // Track search analytics
      if (user) {
        await SearchDiscoveryService.trackSearchClick(user.uid, query, results[0]?.id || '', 0);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAdvancedSearch = (results: SearchResult[], query: string, filters: SearchFilters) => {
    setSearchResults(results);
    setSearchQuery(query);
    setCurrentFilters(filters);
    setHasSearched(true);
    setActiveTab(0); // Switch to Search Results tab
    setSearchStats({
      totalResults: results.length,
      searchTime: 0,
      filtersApplied: Object.values(filters).filter(value => 
        Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null
      ).length
    });
  };

  const handleContentClick = (contentId: string) => {
    navigate(`/content/${contentId}`);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setHasSearched(false);
    setCurrentFilters({});
    setSearchParams({});
    setActiveTab(0); // Switch to Trending tab (index 0 when hasSearched is false)
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const renderSearchResult = (result: SearchResult) => (
    <Box
      key={result.id}
      bg={cardBg}
      borderRadius="lg"
      overflow="hidden"
      border="1px"
      borderColor={borderColor}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
      onClick={() => handleContentClick(result.id)}
    >
      <HStack align="stretch">
        <Image
          src={result.poster}
          alt={result.title}
          w="120px"
          h="80px"
          objectFit="cover"
          fallbackSrc="/placeholder-poster.jpg"
        />
        <VStack p={4} align="stretch" flex={1} gap={2}>
          <HStack justify="space-between">
            <Text fontWeight="bold" fontSize="sm" noOfLines={1} color={textColor}>
              {result.title}
            </Text>
            <Badge colorScheme="purple" fontSize="xs">
              {result.category}
            </Badge>
          </HStack>
          
          <Text fontSize="xs" color={descriptionColor} noOfLines={2}>
            {result.description}
          </Text>
          
          <HStack justify="space-between" fontSize="xs" color={statsColor}>
            <HStack>
              <IconWrapper icon={FiEye} size={12} />
              <Text>{formatNumber(result.views)}</Text>
            </HStack>
            <HStack>
              <IconWrapper icon={FiHeart} size={12} />
              <Text>{formatNumber(result.likes)}</Text>
            </HStack>
            <Text>{result.year}</Text>
          </HStack>
          
          <HStack gap={1} flexWrap="wrap">
            {result.genres.slice(0, 3).map(genre => (
              <Badge key={genre} size="xs" colorScheme="gray">
                {genre}
              </Badge>
            ))}
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );

  const renderSearchStats = () => (
    <HStack gap={4} mb={4} flexWrap="wrap">
      <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
        {searchStats.totalResults} results
      </Badge>
      {searchStats.searchTime > 0 && (
        <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
          {searchStats.searchTime}ms
        </Badge>
      )}
      {searchStats.filtersApplied > 0 && (
        <Badge colorScheme="purple" fontSize="sm" px={3} py={1}>
          {searchStats.filtersApplied} filters
        </Badge>
      )}
    </HStack>
  );

  const renderActiveFilters = () => {
    const activeFilters = Object.entries(currentFilters).filter(([key, value]) => 
      Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null
    );

    if (activeFilters.length === 0) return null;

    return (
      <HStack gap={2} mb={4} flexWrap="wrap">
        <Text fontSize="sm" color={filterLabelColor}>Active filters:</Text>
        {activeFilters.map(([key, value]) => (
          <Badge key={key} colorScheme="purple" fontSize="xs">
            {key}: {Array.isArray(value) ? value.join(', ') : value}
          </Badge>
        ))}
        <Button size="xs" variant="ghost" onClick={() => handleSearch(searchQuery, {})}>
          Clear all
        </Button>
      </HStack>
    );
  };

  return (
    <Container maxW="container.xl" py={6}>
      <VStack gap={6} align="stretch">
        {/* Search Header */}
        <VStack gap={4} align="stretch">
          <Heading size="lg" color={headingColor}>
            <IconWrapper icon={FiSearch} size={24} style={{ marginRight: '8px' }} />
            Search & Discovery
          </Heading>
          
          <HStack gap={2}>
            <Box flex={1}>
              <SearchSuggestions
                onSearch={handleSearch}
                onSuggestionClick={(suggestion) => handleSearch(suggestion.text)}
                placeholder="Search for movies, series, comics, novels..."
                initialQuery={searchQuery}
              />
            </Box>
            <Button
              colorScheme="purple"
              variant="outline"
              leftIcon={<IconWrapper icon={FiFilter} size={16} />}
              onClick={() => setIsAdvancedSearchOpen(true)}
            >
              Advanced
            </Button>
          </HStack>
        </VStack>

        {/* Tabs: Search Results, Trending, Recommendations */}
        <Tabs 
          variant="line" 
          colorScheme="purple" 
          index={activeTab} 
          onChange={setActiveTab}
        >
          <TabList>
            {hasSearched && <Tab>Search Results</Tab>}
            <Tab>Trending</Tab>
            <Tab>Recommendations</Tab>
          </TabList>
          
          <TabPanels>
            {/* Search Results Tab */}
        {hasSearched && (
              <TabPanel px={0}>
                <VStack gap={4} align="stretch" pt={4}>
            <HStack justify="space-between">
                    <Text color={textColor} fontSize="lg" fontWeight="bold">
                Search Results for "{searchQuery}"
              </Text>
              <Button size="sm" variant="ghost" onClick={clearSearch}>
                <IconWrapper icon={FiX} size={16} />
              </Button>
            </HStack>
            
            {renderSearchStats()}
            {renderActiveFilters()}
            
            {isSearching ? (
              <Center py={8}>
                <Spinner size="lg" color="purple.400" />
              </Center>
            ) : searchResults.length === 0 ? (
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  No results found for "{searchQuery}". Try different keywords or use advanced search.
                </AlertDescription>
              </Alert>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                {searchResults.map(renderSearchResult)}
              </SimpleGrid>
            )}
          </VStack>
              </TabPanel>
        )}

            {/* Trending Tab */}
              <TabPanel px={0}>
                <TrendingContent showHeader={false} />
              </TabPanel>
            
            {/* Recommendations Tab */}
              <TabPanel px={0}>
                <ContentRecommendations showHeader={false} />
              </TabPanel>
            </TabPanels>
          </Tabs>
      </VStack>

      {/* Advanced Search Modal */}
      <AdvancedSearchModal
        isOpen={isAdvancedSearchOpen}
        onClose={() => setIsAdvancedSearchOpen(false)}
        onSearchResults={handleAdvancedSearch}
      />
    </Container>
  );
};

export default EnhancedSearch;
